/**
 * useTabLayout — Shared hook for all PageBuilder-based tabs.
 *
 * Loads and persists a TabLayout from monday Storage for the given tab.
 * Falls back to the active preset's default layout on first load.
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storageGetJSON, storageSetJSON } from '../../core/api/client';
import { STORAGE_KEYS } from '../../core/utils/defaults';
import { getPresetTabLayout } from '../../widget-framework/presets';
import { useAppStore } from '../../core/store/appStore';
import type { TabLayout, WidgetGridItem } from '../../core/types';

/** Migrate deprecated widget IDs in a stored layout (e.g. mini_calendar → google_calendar). */
function migrateLayout(layout: TabLayout | WidgetGridItem[] | unknown): TabLayout {
  // Handle legacy format: plain array of WidgetGridItem[] (stored before TabLayout type)
  const isArray = Array.isArray(layout);
  const widgets: WidgetGridItem[] = isArray
    ? (layout as WidgetGridItem[])
    : (layout as TabLayout)?.widgets;

  if (!Array.isArray(widgets)) return layout as TabLayout;

  const RENAMES: Record<string, string> = { mini_calendar: 'google_calendar' };
  let changed = false;
  const migrated = widgets.map((w) => {
    const newId = RENAMES[w.widgetId];
    if (newId) {
      changed = true;
      return {
        ...w,
        widgetId: newId,
        instanceId: w.instanceId.replace(w.widgetId, newId),
      };
    }
    return w;
  });

  if (!changed) return isArray ? { tabId: '', widgets } : (layout as TabLayout);
  return isArray ? { tabId: '', widgets: migrated } : { ...(layout as TabLayout), widgets: migrated };
}

export function useTabLayout(tabId: string) {
  const queryClient = useQueryClient();
  const { context, settings, configPanelInstanceId, setConfigPanel } = useAppStore();
  const userId = context?.user?.id ?? null;
  const storageKey = userId ? STORAGE_KEYS.layout(userId, tabId) : null;

  const { data: layout, isLoading } = useQuery<TabLayout>({
    queryKey: ['tab_layout', tabId, userId],
    queryFn: async () => {
      if (!storageKey) return getPresetTabLayout('default', tabId);
      const stored = await storageGetJSON<TabLayout>(storageKey);
      if (stored) {
        const migrated = migrateLayout(stored);
        // Persist migrated layout back so migration only runs once
        if (migrated !== stored && storageKey) {
          storageSetJSON(storageKey, migrated).catch(() => null);
        }
        return migrated;
      }
      return getPresetTabLayout(settings?.activePreset ?? 'default', tabId);
    },
    enabled: Boolean(userId),
    staleTime: Infinity,
  });

  const saveMutation = useMutation({
    mutationFn: (updated: TabLayout) => {
      if (!storageKey) return Promise.resolve();
      return storageSetJSON(storageKey, updated);
    },
    onSuccess: (_, updated) => {
      queryClient.setQueryData(['tab_layout', tabId, userId], updated);
    },
  });

  const handleLayoutChange = useCallback(
    (updated: TabLayout) => saveMutation.mutate(updated),
    [saveMutation]
  );

  const handleOpenConfig = useCallback(
    (instanceId: string) => {
      const item = layout?.widgets.find((w) => w.instanceId === instanceId);
      if (item) setConfigPanel(instanceId, item.widgetId);
    },
    [layout, setConfigPanel]
  );

  const handleApplyConfig = useCallback(
    (instanceId: string, newConfig: Record<string, unknown>) => {
      if (!layout) return;
      const updated: TabLayout = {
        ...layout,
        widgets: layout.widgets.map((w): WidgetGridItem =>
          w.instanceId === instanceId ? { ...w, config: newConfig } : w
        ),
      };
      saveMutation.mutate(updated);
    },
    [layout, saveMutation]
  );

  // Fall back to default preset when storage layout is unavailable (e.g. userId null)
  const effectiveLayout = useMemo(
    () => layout ?? getPresetTabLayout('default', tabId),
    [layout, tabId],
  );

  const configItem = configPanelInstanceId
    ? effectiveLayout.widgets.find((w) => w.instanceId === configPanelInstanceId)
    : null;

  return {
    layout: effectiveLayout,
    isLoading,
    context,
    configPanelInstanceId: configPanelInstanceId ?? null,
    configItem: configItem ?? null,
    handleLayoutChange,
    handleOpenConfig,
    handleApplyConfig,
    closeConfigPanel: () => setConfigPanel(null, null),
  };
}
